import { DeleteOutlined, PlusOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { listFeeTariff, replaceFeeTariff } from '@api/generated/sdk.gen';
import { designTokens } from '@app/theme';
import { usePermissions } from '@refinedev/core';
import { Permissions } from '@shared/constants/permissions';
import { parseApiError } from '@shared/lib/api-error';
import { formatDateTime } from '@shared/lib/format-date';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Empty,
  InputNumber,
  Popconfirm,
  Space,
  Spin,
  Tabs,
  Typography,
  Upload,
  notification,
} from 'antd';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { FeeTariffResponse } from '@api/generated/types.gen';

import {
  AGREEMENT_TYPES,
  gridFingerprint,
  gridFromResponse,
  gridToRequestRows,
  isGridValid,
  nextRowKey,
  parseTariffCsv,
  renumberMins,
  sumToTiyin,
  tiyinToSum,
  validateGrid,
  type AgreementType,
  type TariffGrid,
  type TariffRow,
} from './lib';

const { Title, Text } = Typography;

const TARIFFS_KEY = ['admin-fee-tariffs'];

/** Группировка разрядов в инпуте сумм: `10 000 000` (дробная часть не трогается). */
function formatSumInput(v: string | number | undefined): string {
  const parts = String(v ?? '').split('.');
  const grouped = (parts[0] ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return parts.length > 1 ? `${grouped}.${parts[1] ?? ''}` : grouped;
}

/**
 * Обратный разбор для `InputNumber`: убирает пробелы группировки.
 * Возвращает строку (antd корректно переводит её в number, а пустую — в null),
 * поэтому приведение типа — осознанное.
 */
const parseSumInput = ((v: string | undefined) =>
  (v ?? '').replace(/\s/g, '')) as unknown as (v: string | undefined) => number;

/**
 * Раздел «Тарифы комиссии»: сетка service fee — диапазон суммы займа ×
 * тип договора → фиксированная комиссия.
 *
 * Чтение — `settings.read`, сохранение — `settings.write`. API заменяет сетку
 * только целиком (`PUT /api/admin/fee-tariffs`): построчного редактирования нет
 * намеренно. Клиентская валидация зеркалит серверную (`validateGrid`) и
 * подсвечивает проблемные строки до отправки; причина серверного
 * `FEE_TARIFF_GRID_INVALID` показывается рядом с сеткой как есть.
 *
 * Все суммы хранятся в тийинах, вводятся и отображаются в сумах (÷100).
 * Смена сетки действует только на новые подписания.
 */
export function FeeTariffsPage() {
  const { t } = useTranslation(['feeTariffs', 'common', 'errors']);
  const qc = useQueryClient();
  const { data: perms } = usePermissions<string[]>({});
  const canWrite = perms?.includes(Permissions.SETTINGS_WRITE) ?? false;

  const [grid, setGrid] = useState<TariffGrid | null>(null);
  const [baseline, setBaseline] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<AgreementType>('LOAN');

  const tariffsQuery = useQuery({
    queryKey: TARIFFS_KEY,
    queryFn: async (): Promise<FeeTariffResponse[]> => {
      const res = await listFeeTariff();
      return res.data ?? [];
    },
  });

  useEffect(() => {
    if (tariffsQuery.data && grid === null) {
      const g = gridFromResponse(tariffsQuery.data);
      setGrid(g);
      setBaseline(gridFingerprint(g));
    }
  }, [tariffsQuery.data, grid]);

  const errors = useMemo(
    () => (grid ? validateGrid(grid) : { rows: {}, global: [] }),
    [grid],
  );
  const valid = isGridValid(errors);
  const dirty = grid !== null && gridFingerprint(grid) !== baseline;

  const lastUpdatedAt = useMemo(() => {
    const stamps = (tariffsQuery.data ?? [])
      .map((r) => r.updatedAt)
      .filter((s): s is string => Boolean(s))
      .sort();
    return stamps.length > 0 ? stamps[stamps.length - 1] : null;
  }, [tariffsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async (g: TariffGrid): Promise<FeeTariffResponse[]> => {
      const res = await replaceFeeTariff({ body: { rows: gridToRequestRows(g) } });
      return res.data ?? [];
    },
    onSuccess: (rows) => {
      qc.setQueryData(TARIFFS_KEY, rows);
      const g = gridFromResponse(rows);
      setGrid(g);
      setBaseline(gridFingerprint(g));
      setServerError(null);
      notification.success({ message: t('feeTariffs:saved'), placement: 'topRight' });
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: unknown; status?: number } };
      const apiErr = parseApiError(e.response?.data, e.response?.status);
      setServerError(apiErr.code === 'FEE_TARIFF_GRID_INVALID' ? (apiErr.message ?? '') : null);
      notification.error({
        message: t(`errors:${apiErr.code}`, { defaultValue: t('errors:UNKNOWN_ERROR') }),
        placement: 'topRight',
      });
    },
  });

  const updateRow = (
    type: AgreementType,
    key: string,
    field: 'minTiyin' | 'maxTiyin' | 'feeTiyin',
    value: number | null,
  ): void => {
    setGrid((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [type]: prev[type].map((r) => (r.key === key ? { ...r, [field]: value } : r)),
      };
    });
  };

  const addRow = (type: AgreementType): void => {
    setGrid((prev) => {
      if (!prev) return prev;
      const rows = prev[type];
      const prevMax = rows.at(-1)?.maxTiyin ?? null;
      const newRow: TariffRow = {
        key: nextRowKey(),
        minTiyin: rows.length === 0 ? 0 : prevMax !== null ? prevMax + 1 : null,
        maxTiyin: null,
        feeTiyin: null,
      };
      return { ...prev, [type]: [...rows, newRow] };
    });
  };

  const removeRow = (type: AgreementType, key: string): void => {
    setGrid((prev) => {
      if (!prev) return prev;
      return { ...prev, [type]: prev[type].filter((r) => r.key !== key) };
    });
  };

  const resetGrid = (): void => {
    const g = gridFromResponse(tariffsQuery.data ?? []);
    setGrid(g);
    setBaseline(gridFingerprint(g));
    setServerError(null);
  };

  const importCsv = async (file: File): Promise<void> => {
    const text = await file.text();
    const result = parseTariffCsv(text);
    if (!result.ok) {
      notification.error({
        message: t(`feeTariffs:import.errors.${result.error}`, {
          line: result.line ?? 0,
        }),
        placement: 'topRight',
      });
      return;
    }
    setGrid(result.grid);
    setServerError(null);
    const count = result.grid.LOAN.length + result.grid.INSTALLMENT.length;
    notification.info({
      message: t('feeTariffs:import.done', { count }),
      placement: 'topRight',
    });
  };

  const inputStyle = {
    width: '100%',
    fontFamily: '"JetBrains Mono", monospace',
    fontVariantNumeric: 'tabular-nums',
    fontSize: 13,
  } as const;

  const renderRows = (type: AgreementType): ReactNode => {
    const rows = grid?.[type] ?? [];
    if (rows.length === 0) {
      return (
        <div style={{ padding: 32 }}>
          <Empty description={t('feeTariffs:empty')} />
        </div>
      );
    }
    return (
      <>
        <div
          style={{
            display: 'flex',
            gap: 8,
            padding: '8px 16px',
            background: designTokens.slate50,
            borderBlockEnd: `1px solid ${designTokens.slate200}`,
          }}
        >
          {(['min', 'max', 'fee'] as const).map((col) => (
            <Text
              key={col}
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 12,
                fontWeight: 500,
                color: designTokens.slate500,
              }}
            >
              {t(`feeTariffs:columns.${col}`)}
            </Text>
          ))}
          {canWrite && <div style={{ width: 24, flexShrink: 0 }} />}
        </div>
        {rows.map((row, idx) => {
          const rowErrors = errors.rows[row.key] ?? [];
          const hasError = rowErrors.length > 0;
          return (
            <div
              key={row.key}
              style={{
                padding: '6px 16px',
                borderBlockStart: idx > 0 ? `1px solid ${designTokens.slate100}` : 'none',
                background: hasError ? '#FEF2F2' : undefined,
              }}
            >
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {(['minTiyin', 'maxTiyin', 'feeTiyin'] as const).map((field) => (
                  <div key={field} style={{ flex: 1, minWidth: 0 }}>
                    <InputNumber<number>
                      size="small"
                      style={inputStyle}
                      value={row[field] === null ? undefined : tiyinToSum(row[field])}
                      onChange={(v) => {
                        updateRow(type, row.key, field, v === null ? null : sumToTiyin(v));
                      }}
                      min={0}
                      formatter={formatSumInput}
                      parser={parseSumInput}
                      status={hasError ? 'error' : undefined}
                      disabled={!canWrite}
                      controls={false}
                    />
                  </div>
                ))}
                {canWrite && (
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    aria-label={t('feeTariffs:removeRow')}
                    style={{ width: 24, flexShrink: 0, padding: 0 }}
                    onClick={() => {
                      removeRow(type, row.key);
                    }}
                  />
                )}
              </div>
              {hasError && (
                <Text style={{ display: 'block', marginBlockStart: 2, fontSize: 12 }} type="danger">
                  {rowErrors.map((code) => t(`feeTariffs:validation.${code}`)).join(' ')}
                </Text>
              )}
            </div>
          );
        })}
        {canWrite && (
          <div style={{ padding: '8px 16px', borderBlockStart: `1px solid ${designTokens.slate100}` }}>
            <Button
              type="dashed"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => {
                addRow(type);
              }}
            >
              {t('feeTariffs:addRow')}
            </Button>
          </div>
        )}
      </>
    );
  };

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <div>
        <Title level={3} style={{ marginBlockEnd: 4, fontWeight: 600 }}>
          {t('feeTariffs:title')}
        </Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          {t('feeTariffs:subtitle')}
        </Text>
        {lastUpdatedAt && (
          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBlockStart: 4 }}>
            {t('feeTariffs:lastUpdated', { date: formatDateTime(lastUpdatedAt) })}
          </Text>
        )}
      </div>

      {serverError !== null && (
        <Alert
          type="error"
          showIcon
          message={t('errors:FEE_TARIFF_GRID_INVALID')}
          description={serverError || undefined}
          closable
          onClose={() => {
            setServerError(null);
          }}
        />
      )}

      {errors.global.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={errors.global
            .map((code) => t(`feeTariffs:validation.${code}`))
            .join(' ')}
        />
      )}

      {canWrite && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Upload
            accept=".csv,text/csv"
            showUploadList={false}
            beforeUpload={(file) => {
              void importCsv(file);
              return false;
            }}
          >
            <Button icon={<UploadOutlined />}>{t('feeTariffs:import.button')}</Button>
          </Upload>
          <Button
            disabled={valid}
            onClick={() => {
              setGrid((prev) => (prev ? renumberMins(prev) : prev));
            }}
          >
            {t('feeTariffs:renumber')}
          </Button>
          <Button disabled={!dirty} onClick={resetGrid}>
            {t('feeTariffs:reset')}
          </Button>
          <Popconfirm
            title={t('feeTariffs:save.confirmTitle')}
            description={t('feeTariffs:save.confirmDescription')}
            okText={t('common:actions.save')}
            cancelText={t('common:actions.cancel')}
            onConfirm={() => {
              if (grid) saveMutation.mutate(grid);
            }}
            disabled={!dirty || !valid}
          >
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saveMutation.isPending}
              disabled={!dirty || !valid}
            >
              {t('feeTariffs:save.button')}
            </Button>
          </Popconfirm>
        </div>
      )}

      <Card style={{ borderColor: designTokens.slate200 }} styles={{ body: { padding: 0 } }}>
        <Spin spinning={tariffsQuery.isLoading}>
          <Tabs
            activeKey={activeType}
            onChange={(key) => {
              setActiveType(key as AgreementType);
            }}
            tabBarStyle={{ margin: 0, paddingInline: 16 }}
            items={AGREEMENT_TYPES.map((type) => ({
              key: type,
              label: `${t(`feeTariffs:types.${type}`)} · ${String(grid?.[type].length ?? 0)}`,
              children: renderRows(type),
            }))}
          />
        </Spin>
      </Card>
    </Space>
  );
}
